import { JOINT_DEFINITIONS, type JointKey } from '../modules/JointAngles';
import type { DecodedPoseBinary } from '../modules/PoseBinaryFormat';

export interface PoseExportFrame {
  t: number;
  angles?: Partial<Record<JointKey, number>>;
  anglesPerPerson?: (Partial<Record<JointKey, number>> | undefined)[];
  poses: {
    fps: number;
    inferenceTimeMs: number;
    nodes: {
      id: number;
      name: string;
      x: number;
      y: number;
      score: number;
      worldX?: number;
      worldY?: number;
      worldZ?: number;
      worldVisibility?: number;
    }[];
  }[];
}

export interface PoseExportFile {
  exportedAt: string;
  appMode?: string;
  engineId?: string;
  engineLabel?: string;
  source?: string;
  resolution?: { width: number; height: number };
  frames: PoseExportFrame[];
}

export interface PoseEdge {
  a: number;
  b: number;
}

/**
 * Structure-of-Arrays representation for fast per-frame access.
 * All time values are in seconds, relative to the first frame (t0 = 0).
 */
export interface PoseData {
  meta: {
    engineId?: string;
    engineLabel?: string;
    resolution?: { width: number; height: number };
    numFrames: number;
    numNodes: number;
    duration: number;
    fps: number;
    nodeNames: string[];
    edges: PoseEdge[];
    angleKeys: JointKey[];
    /** True when worldX/Y/Z are populated (MediaPipe only). */
    hasWorld: boolean;
  };
  /** [numFrames] timestamps in seconds (relative) */
  t: Float32Array;
  /** [numFrames * numNodes] normalized x in [0,1] */
  x: Float32Array;
  /** [numFrames * numNodes] normalized y in [0,1] */
  y: Float32Array;
  /** [numFrames * numNodes] confidence score */
  score: Float32Array;
  /** [numFrames * numAngles] angle in degrees, NaN if missing */
  angles: Float32Array;
  /** [numFrames * numNodes] world-space coords in meters (hip-centered, y-up).
   *  Undefined when the recording has no 3D data. */
  worldX?: Float32Array;
  worldY?: Float32Array;
  worldZ?: Float32Array;
  /** [numFrames * numNodes] world visibility in [0,1]. */
  worldVisibility?: Float32Array;
}

const MEDIAPIPE_NODE_NAMES = [
  'nose',
  'left_eye_inner',
  'left_eye',
  'left_eye_outer',
  'right_eye_inner',
  'right_eye',
  'right_eye_outer',
  'left_ear',
  'right_ear',
  'mouth_left',
  'mouth_right',
  'left_shoulder',
  'right_shoulder',
  'left_elbow',
  'right_elbow',
  'left_wrist',
  'right_wrist',
  'left_pinky',
  'right_pinky',
  'left_index',
  'right_index',
  'left_thumb',
  'right_thumb',
  'left_hip',
  'right_hip',
  'left_knee',
  'right_knee',
  'left_ankle',
  'right_ankle',
  'left_heel',
  'right_heel',
  'left_foot_index',
  'right_foot_index',
];

// MediaPipe 33-point skeleton edges.
const MEDIAPIPE_EDGES: PoseEdge[] = [
  // face
  { a: 0, b: 1 },
  { a: 1, b: 2 },
  { a: 2, b: 3 },
  { a: 3, b: 7 },
  { a: 0, b: 4 },
  { a: 4, b: 5 },
  { a: 5, b: 6 },
  { a: 6, b: 8 },
  { a: 9, b: 10 },
  // torso
  { a: 11, b: 12 },
  { a: 11, b: 23 },
  { a: 12, b: 24 },
  { a: 23, b: 24 },
  // left arm
  { a: 11, b: 13 },
  { a: 13, b: 15 },
  { a: 15, b: 17 },
  { a: 15, b: 19 },
  { a: 15, b: 21 },
  { a: 17, b: 19 },
  // right arm
  { a: 12, b: 14 },
  { a: 14, b: 16 },
  { a: 16, b: 18 },
  { a: 16, b: 20 },
  { a: 16, b: 22 },
  { a: 18, b: 20 },
  // left leg
  { a: 23, b: 25 },
  { a: 25, b: 27 },
  { a: 27, b: 29 },
  { a: 27, b: 31 },
  { a: 29, b: 31 },
  // right leg
  { a: 24, b: 26 },
  { a: 26, b: 28 },
  { a: 28, b: 30 },
  { a: 28, b: 32 },
  { a: 30, b: 32 },
];

const MOVENET_NODE_NAMES = [
  'nose',
  'left_eye',
  'right_eye',
  'left_ear',
  'right_ear',
  'left_shoulder',
  'right_shoulder',
  'left_elbow',
  'right_elbow',
  'left_wrist',
  'right_wrist',
  'left_hip',
  'right_hip',
  'left_knee',
  'right_knee',
  'left_ankle',
  'right_ankle',
];

const MOVENET_EDGES: PoseEdge[] = [
  { a: 0, b: 1 },
  { a: 0, b: 2 },
  { a: 1, b: 3 },
  { a: 2, b: 4 },
  { a: 5, b: 6 },
  { a: 5, b: 11 },
  { a: 6, b: 12 },
  { a: 11, b: 12 },
  { a: 5, b: 7 },
  { a: 7, b: 9 },
  { a: 6, b: 8 },
  { a: 8, b: 10 },
  { a: 11, b: 13 },
  { a: 13, b: 15 },
  { a: 12, b: 14 },
  { a: 14, b: 16 },
];

function pickSkeleton(
  engineId: string | undefined,
  numNodes: number,
): {
  nodeNames: string[];
  edges: PoseEdge[];
} {
  if (engineId === 'movenet' || numNodes === 17) {
    return { nodeNames: MOVENET_NODE_NAMES, edges: MOVENET_EDGES };
  }
  return { nodeNames: MEDIAPIPE_NODE_NAMES, edges: MEDIAPIPE_EDGES };
}

const ANGLE_KEYS = JOINT_DEFINITIONS.map((d) => d.key);

/**
 * Build a packed SoA PoseData from the raw exported JSON.
 * Picks the primary person per frame (index 0).
 * Time is normalized so the first frame is t=0.
 */
export function buildPoseData(file: PoseExportFile): PoseData {
  const frames = file.frames ?? [];
  if (frames.length === 0) {
    throw new Error('no frames in pose data');
  }
  const firstPose = frames.find((f) => f.poses && f.poses.length > 0)?.poses[0];
  if (!firstPose) throw new Error('no poses in any frame');

  const numNodes = firstPose.nodes.length;
  const { nodeNames, edges } = pickSkeleton(file.engineId, numNodes);
  const numFrames = frames.length;
  const t0 = frames[0].t;

  const hasWorld = firstPose.nodes.some((n) => typeof n.worldX === 'number');

  const t = new Float32Array(numFrames);
  const x = new Float32Array(numFrames * numNodes);
  const y = new Float32Array(numFrames * numNodes);
  const score = new Float32Array(numFrames * numNodes);
  const angles = new Float32Array(numFrames * ANGLE_KEYS.length);
  const worldX = hasWorld ? new Float32Array(numFrames * numNodes) : undefined;
  const worldY = hasWorld ? new Float32Array(numFrames * numNodes) : undefined;
  const worldZ = hasWorld ? new Float32Array(numFrames * numNodes) : undefined;
  const worldVisibility = hasWorld ? new Float32Array(numFrames * numNodes) : undefined;
  if (hasWorld && worldX && worldY && worldZ) {
    worldX.fill(Number.NaN);
    worldY.fill(Number.NaN);
    worldZ.fill(Number.NaN);
  }

  for (let fi = 0; fi < numFrames; fi += 1) {
    const frame = frames[fi];
    t[fi] = (frame.t - t0) / 1000; // ms -> seconds
    const pose = frame.poses && frame.poses[0];
    const nodeOffset = fi * numNodes;
    if (pose) {
      const nodes = pose.nodes;
      for (let ni = 0; ni < numNodes; ni += 1) {
        const n = nodes[ni];
        if (!n) continue;
        x[nodeOffset + ni] = n.x;
        y[nodeOffset + ni] = n.y;
        score[nodeOffset + ni] = n.score;
        if (hasWorld && worldX && worldY && worldZ) {
          if (typeof n.worldX === 'number') worldX[nodeOffset + ni] = n.worldX;
          if (typeof n.worldY === 'number') worldY[nodeOffset + ni] = n.worldY;
          if (typeof n.worldZ === 'number') worldZ[nodeOffset + ni] = n.worldZ;
          if (worldVisibility && typeof n.worldVisibility === 'number') {
            worldVisibility[nodeOffset + ni] = n.worldVisibility;
          }
        }
      }
    }
    const angleOffset = fi * ANGLE_KEYS.length;
    const a = frame.angles ?? frame.anglesPerPerson?.[0];
    for (let ki = 0; ki < ANGLE_KEYS.length; ki += 1) {
      const v = a?.[ANGLE_KEYS[ki]];
      angles[angleOffset + ki] = typeof v === 'number' ? v : Number.NaN;
    }
  }

  const duration = t[numFrames - 1] - t[0];
  const fps = duration > 0 ? (numFrames - 1) / duration : 0;

  return {
    meta: {
      engineId: file.engineId,
      engineLabel: file.engineLabel,
      resolution: file.resolution,
      numFrames,
      numNodes,
      duration,
      fps,
      nodeNames,
      edges,
      angleKeys: ANGLE_KEYS,
      hasWorld,
    },
    t,
    x,
    y,
    score,
    angles,
    worldX,
    worldY,
    worldZ,
    worldVisibility,
  };
}

/**
 * Build PoseData from the already-decoded binary representation.
 * Timestamps in DecodedPoseBinary are ms relative to recordingStartedAt so
 * they line up with video currentTime directly.
 */
export function buildPoseDataFromBinary(decoded: DecodedPoseBinary): PoseData {
  const { numFrames, numNodes, timestamps, angleKeys } = decoded;
  const numAngles = angleKeys.length;
  const { nodeNames, edges } = pickSkeleton(decoded.engineId, numNodes);

  const t = new Float32Array(numFrames);
  for (let i = 0; i < numFrames; i += 1) t[i] = timestamps[i] / 1000;

  // Reorder binary's local angleKey order to the viewer's canonical order
  // so downstream indexing (ANGLE_KEYS) stays consistent.
  const angles = new Float32Array(numFrames * ANGLE_KEYS.length);
  const keyIndex = new Map<JointKey, number>();
  angleKeys.forEach((k, i) => keyIndex.set(k, i));
  for (let fi = 0; fi < numFrames; fi += 1) {
    const srcOff = fi * numAngles;
    const dstOff = fi * ANGLE_KEYS.length;
    for (let ki = 0; ki < ANGLE_KEYS.length; ki += 1) {
      const src = keyIndex.get(ANGLE_KEYS[ki]);
      angles[dstOff + ki] = src === undefined ? Number.NaN : decoded.angles[srcOff + src];
    }
  }

  const duration = t[numFrames - 1] - t[0];
  const fps = duration > 0 ? (numFrames - 1) / duration : 0;

  return {
    meta: {
      engineId: decoded.engineId,
      engineLabel: decoded.engineLabel,
      resolution: decoded.resolution,
      numFrames,
      numNodes,
      duration,
      fps,
      nodeNames,
      edges,
      angleKeys: ANGLE_KEYS,
      hasWorld: decoded.hasWorld,
    },
    t,
    x: decoded.x,
    y: decoded.y,
    score: decoded.score,
    angles,
    worldX: decoded.worldX,
    worldY: decoded.worldY,
    worldZ: decoded.worldZ,
    worldVisibility: decoded.worldVisibility,
  };
}

/**
 * Binary search: return the largest index i such that t[i] <= tSec.
 * Returns 0 if tSec is before the first frame, numFrames-1 if after.
 */
export function findFrameIndex(data: PoseData, tSec: number): number {
  const t = data.t;
  const n = t.length;
  if (n === 0) return 0;
  if (tSec <= t[0]) return 0;
  if (tSec >= t[n - 1]) return n - 1;
  let lo = 0;
  let hi = n - 1;
  while (lo + 1 < hi) {
    const mid = (lo + hi) >> 1;
    if (t[mid] <= tSec) lo = mid;
    else hi = mid;
  }
  return lo;
}


